/**
 * BankStore - MobX store for bank/inventory management.
 *
 * Manages character bank with localStorage-first pattern:
 * - All changes saved to localStorage immediately (works offline)
 * - Server sync as backup when authenticated
 */

import { MAX_BANK_ITEMS, STARTING_ITEMS } from '@idle-depths/shared';
import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';
import type {
  AddItemResult,
  BankItem,
  EquipmentSlot,
  EquipResult,
  ItemDefinition,
  ItemType,
} from '@idle-depths/shared';

import { getVanillaClient } from '@/lib/trpc';
import { BankStorageService } from '@/services/BankStorageService';

/**
 * Sort options for bank items.
 */
export type BankSortOption = 'name' | 'type' | 'value' | 'acquired';

/**
 * BankStore manages the character's bank items.
 */
export class BankStore {
  /** Items indexed by item ID */
  items: Map<string, BankItem> = new Map();

  /** Whether loading bank data */
  isLoading = false;

  /** Whether syncing to server */
  isSyncing = false;

  /** Last error */
  error: string | null = null;

  /** Sync debounce timer */
  private syncTimer: number | null = null;

  /** Sync debounce delay (ms) */
  private readonly SYNC_DEBOUNCE = 2000;

  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
      SYNC_DEBOUNCE: false,
    } as Record<string, false>);
  }

  // ===========================================================================
  // Computed Properties
  // ===========================================================================

  /**
   * Get all items as a sorted array.
   *
   * Default sort: by type, then by name.
   */
  get sortedItems(): BankItem[] {
    return Array.from(this.items.values()).sort((a, b) => {
      const definitionA = this.getItemDefinition(a.itemId);
      const definitionB = this.getItemDefinition(b.itemId);

      if (!definitionA || !definitionB) {
        return 0;
      }

      // Sort by type first, then by name
      if (definitionA.type !== definitionB.type) {
        return definitionA.type.localeCompare(definitionB.type);
      }
      return definitionA.name.localeCompare(definitionB.name);
    });
  }

  /**
   * Get unique item count (number of different item types).
   */
  get itemCount(): number {
    return this.items.size;
  }

  /**
   * Get total item quantity (sum of all stacks).
   */
  get totalQuantity(): number {
    let total = 0;
    for (const item of this.items.values()) {
      total += item.quantity;
    }
    return total;
  }

  // ===========================================================================
  // Filtering & Sorting
  // ===========================================================================

  /**
   * Get items by category.
   *
   * @param category - Item type or 'all'
   * @returns Filtered and sorted items
   */
  getItemsByCategory(category: ItemType | 'all'): BankItem[] {
    if (category === 'all') {
      return this.sortedItems;
    }

    return this.sortedItems.filter((item) => {
      const definition = this.getItemDefinition(item.itemId);
      return definition?.type === category;
    });
  }

  /**
   * Search items by name.
   *
   * @param query - Search query (case-insensitive)
   * @returns Matching items
   */
  searchItems(query: string): BankItem[] {
    const lowerQuery = query.toLowerCase().trim();

    if (!lowerQuery) {
      return this.sortedItems;
    }

    return this.sortedItems.filter((item) => {
      const definition = this.getItemDefinition(item.itemId);
      return definition?.name.toLowerCase().includes(lowerQuery);
    });
  }

  /**
   * Sort items by a specific option.
   *
   * @param items - Items to sort
   * @param sortBy - Sort option
   * @param ascending - Sort direction
   * @returns Sorted items
   */
  sortItems(items: BankItem[], sortBy: BankSortOption, ascending = true): BankItem[] {
    const sorted = [...items].sort((a, b) => {
      const definitionA = this.getItemDefinition(a.itemId);
      const definitionB = this.getItemDefinition(b.itemId);

      if (!definitionA || !definitionB) {
        return 0;
      }

      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = definitionA.name.localeCompare(definitionB.name);
          break;
        case 'type':
          comparison = definitionA.type.localeCompare(definitionB.type);
          if (comparison === 0) {
            comparison = definitionA.name.localeCompare(definitionB.name);
          }
          break;
        case 'value':
          comparison = definitionA.value - definitionB.value;
          break;
        case 'acquired':
          comparison = a.acquiredAt - b.acquiredAt;
          break;
      }

      return ascending ? comparison : -comparison;
    });

    return sorted;
  }

  // ===========================================================================
  // Computed Properties - Capacity
  // ===========================================================================

  /**
   * Get maximum bank capacity.
   */
  get maxCapacity(): number {
    return MAX_BANK_ITEMS;
  }

  /**
   * Get remaining capacity (slots available).
   */
  get remainingCapacity(): number {
    return Math.max(0, this.maxCapacity - this.items.size);
  }

  /**
   * Check if bank is at capacity.
   */
  get isAtCapacity(): boolean {
    return this.items.size >= this.maxCapacity;
  }

  /**
   * Get capacity usage as percentage (0-100).
   */
  get capacityPercentage(): number {
    return Math.min(100, (this.items.size / this.maxCapacity) * 100);
  }

  // ===========================================================================
  // Item Management
  // ===========================================================================

  /**
   * Add item to bank.
   *
   * Handles stacking for stackable items correctly:
   * - Stackable items: Add to existing stack (up to maxStackSize)
   * - Non-stackable items: Only allow quantity of 1, refuse if already owned
   *
   * @param itemId - Item definition ID
   * @param quantity - Quantity to add
   * @returns Result object with success status and details
   */
  addItem(itemId: string, quantity: number): AddItemResult {
    if (quantity <= 0) {
      return { success: false, error: 'Invalid quantity', addedQuantity: 0 };
    }

    const definition = this.getItemDefinition(itemId);
    if (!definition) {
      return { success: false, error: 'Item definition not found', addedQuantity: 0 };
    }

    const existing = this.items.get(itemId);
    const now = Date.now();

    // Handle non-stackable items
    if (!definition.stackable) {
      if (existing) {
        // Non-stackable item already in bank - cannot add more
        return {
          success: false,
          error: `Cannot stack ${definition.name} - already owned`,
          addedQuantity: 0,
        };
      }

      // Check capacity for new item slot
      if (this.isAtCapacity) {
        return { success: false, error: 'Bank is full', addedQuantity: 0 };
      }

      // Non-stackable items can only be added with quantity 1
      this.items.set(itemId, {
        itemId,
        quantity: 1,
        acquiredAt: now,
        updatedAt: now,
      });

      this.saveToLocalStorage();
      this.queueServerSync();
      return { success: true, addedQuantity: 1 };
    }

    // Handle stackable items
    if (existing) {
      // Calculate how many we can add
      const maxStack = definition.maxStackSize ?? Infinity;
      const spaceInStack = maxStack - existing.quantity;
      const toAdd = Math.min(quantity, spaceInStack);

      if (toAdd <= 0) {
        return { success: false, error: 'Stack is full', addedQuantity: 0 };
      }

      existing.quantity += toAdd;
      existing.updatedAt = now;

      this.saveToLocalStorage();
      this.queueServerSync();

      return {
        success: true,
        addedQuantity: toAdd,
        error: toAdd < quantity ? `Only added ${String(toAdd)} (stack limit)` : undefined,
      };
    }

    // New stackable item - check capacity
    if (this.isAtCapacity) {
      return { success: false, error: 'Bank is full', addedQuantity: 0 };
    }

    // Calculate initial quantity (respecting max stack)
    const maxStack = definition.maxStackSize ?? Infinity;
    const initialQuantity = Math.min(quantity, maxStack);

    this.items.set(itemId, {
      itemId,
      quantity: initialQuantity,
      acquiredAt: now,
      updatedAt: now,
    });

    this.saveToLocalStorage();
    this.queueServerSync();

    return {
      success: true,
      addedQuantity: initialQuantity,
      error:
        initialQuantity < quantity
          ? `Only added ${String(initialQuantity)} (stack limit)`
          : undefined,
    };
  }

  /**
   * Remove item from bank.
   *
   * @param itemId - Item definition ID
   * @param quantity - Quantity to remove
   * @returns True if successful
   */
  removeItem(itemId: string, quantity: number): boolean {
    if (quantity <= 0) {
      return false;
    }

    const item = this.items.get(itemId);
    if (!item || item.quantity < quantity) {
      return false;
    }

    item.quantity -= quantity;
    item.updatedAt = Date.now();

    if (item.quantity <= 0) {
      this.items.delete(itemId);
    }

    this.saveToLocalStorage();
    this.queueServerSync();
    return true;
  }

  /**
   * Check if bank has item with minimum quantity.
   *
   * @param itemId - Item definition ID
   * @param quantity - Minimum quantity (default 1)
   * @returns True if has item
   */
  hasItem(itemId: string, quantity = 1): boolean {
    const item = this.items.get(itemId);
    return item ? item.quantity >= quantity : false;
  }

  /**
   * Get quantity of an item.
   *
   * @param itemId - Item definition ID
   * @returns Quantity (0 if not in bank)
   */
  getQuantity(itemId: string): number {
    return this.items.get(itemId)?.quantity ?? 0;
  }

  /**
   * Get item definition from content registry.
   *
   * @param itemId - Item definition ID
   * @returns Item definition or undefined
   */
  getItemDefinition(itemId: string): ItemDefinition | undefined {
    return this.rootStore.gameStore.content.get<ItemDefinition>('items', itemId);
  }

  // ===========================================================================
  // Equipment Management
  // ===========================================================================

  /**
   * Check if an item is equippable.
   *
   * @param itemId - Item definition ID
   * @returns True if item can be equipped
   */
  isEquippable(itemId: string): boolean {
    const definition = this.getItemDefinition(itemId);
    return definition?.equipSlot !== null && definition?.equipSlot !== undefined;
  }

  /**
   * Get the equipment slot for an item.
   *
   * @param itemId - Item definition ID
   * @returns Equipment slot or null if not equippable
   */
  getEquipSlot(itemId: string): EquipmentSlot | null {
    const definition = this.getItemDefinition(itemId);
    return definition?.equipSlot ?? null;
  }

  /**
   * Get the currently equipped item in a slot.
   *
   * @param slot - Equipment slot to check
   * @returns Item ID or null if empty
   */
  getEquippedItem(slot: EquipmentSlot): string | null {
    return this.rootStore.characterStore.activeCharacter?.equipment[slot] ?? null;
  }

  /**
   * Check if an item is currently equipped.
   *
   * @param itemId - Item definition ID
   * @returns True if item is equipped
   */
  isEquipped(itemId: string): boolean {
    const equipment = this.rootStore.characterStore.activeCharacter?.equipment;
    if (!equipment) {
      return false;
    }
    return Object.values(equipment).includes(itemId);
  }

  /**
   * Equip an item from the bank.
   *
   * - Validates item is equippable
   * - Validates item is in bank
   * - Handles swapping with currently equipped item
   *
   * @param itemId - Item definition ID to equip
   * @returns Result object with success status and details
   */
  equipItem(itemId: string): EquipResult {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      return { success: false, error: 'No active character', previousItemId: null };
    }

    const definition = this.getItemDefinition(itemId);
    if (!definition) {
      return { success: false, error: 'Item definition not found', previousItemId: null };
    }

    if (!definition.equipSlot) {
      return {
        success: false,
        error: `${definition.name} cannot be equipped`,
        previousItemId: null,
      };
    }

    // Check if item is in bank
    if (!this.hasItem(itemId)) {
      return { success: false, error: 'Item not in bank', previousItemId: null };
    }

    const slot = definition.equipSlot;
    const previousItemId = character.equipment[slot];

    // Unequip current item first (add back to bank)
    if (previousItemId) {
      // Add previously equipped item back to bank
      const result = this.addItem(previousItemId, 1);
      if (!result.success) {
        return {
          success: false,
          error: `Cannot unequip ${previousItemId}: ${result.error ?? 'unknown error'}`,
          previousItemId: null,
        };
      }
    }

    // Remove new item from bank
    if (!this.removeItem(itemId, 1)) {
      // Rollback: Put previous item back in equipment
      if (previousItemId) {
        this.removeItem(previousItemId, 1);
      }
      return { success: false, error: 'Failed to remove item from bank', previousItemId: null };
    }

    // Update character equipment
    this.rootStore.characterStore.setEquipment(slot, itemId);

    return { success: true, previousItemId };
  }

  /**
   * Unequip an item and put it in the bank.
   *
   * @param slot - Equipment slot to unequip
   * @returns Result object with success status and details
   */
  unequipItem(slot: EquipmentSlot): EquipResult {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      return { success: false, error: 'No active character', previousItemId: null };
    }

    const equippedItemId = character.equipment[slot];
    if (!equippedItemId) {
      return { success: false, error: 'No item equipped in slot', previousItemId: null };
    }

    // Add item to bank
    const result = this.addItem(equippedItemId, 1);
    if (!result.success) {
      return {
        success: false,
        error: result.error ?? 'Failed to add item to bank',
        previousItemId: null,
      };
    }

    // Clear equipment slot
    this.rootStore.characterStore.setEquipment(slot, null);

    return { success: true, previousItemId: equippedItemId };
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Initialize bank for current character.
   *
   * Loads from localStorage, then syncs with server if authenticated.
   * Grants starting items to new characters with empty banks.
   */
  async initialize(): Promise<void> {
    const characterId = this.rootStore.characterStore.activeCharacter?.id;
    if (!characterId) {
      return;
    }

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      // Load from localStorage
      this.loadFromLocalStorage(characterId);

      // Grant starting items to new characters with empty banks
      if (this.items.size === 0) {
        this.grantStartingItems();
      }

      // Sync with server if authenticated
      if (this.rootStore.authStore.hasServerUser) {
        await this.syncWithServer();
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'bank_store_init' },
      });
      runInAction(() => {
        this.error = 'Failed to load bank data';
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Grant starting items to a new character.
   *
   * Called when initializing a character with an empty bank.
   */
  private grantStartingItems(): void {
    // eslint-disable-next-line no-console
    console.info('[BankStore] Granting starting items to new character');

    const now = Date.now();
    for (const [itemId, quantity] of STARTING_ITEMS) {
      this.items.set(itemId, {
        itemId,
        quantity,
        acquiredAt: now,
        updatedAt: now,
      });
    }

    this.saveToLocalStorage();
  }

  // ===========================================================================
  // LocalStorage Operations
  // ===========================================================================

  /**
   * Load bank from localStorage.
   */
  private loadFromLocalStorage(characterId: string): void {
    const items = BankStorageService.getBank(characterId);

    runInAction(() => {
      this.items.clear();
      for (const item of items) {
        this.items.set(item.itemId, item);
      }
    });
  }

  /**
   * Save bank to localStorage.
   */
  private saveToLocalStorage(): void {
    const characterId = this.rootStore.characterStore.activeCharacter?.id;
    if (!characterId) {
      return;
    }

    BankStorageService.setBank(characterId, Array.from(this.items.values()));
  }

  // ===========================================================================
  // Server Sync
  // ===========================================================================

  /**
   * Sync with server using timestamp-based conflict resolution.
   *
   * For each item:
   * - If only on server: Add to local
   * - If only local: Keep local (will push to server)
   * - If on both: Keep the one with newer updatedAt
   */
  private async syncWithServer(): Promise<void> {
    const characterId = this.rootStore.characterStore.activeCharacter?.id;
    if (!characterId || !this.rootStore.authStore.hasServerUser) {
      return;
    }

    runInAction(() => {
      this.isSyncing = true;
    });

    try {
      const client = getVanillaClient();

      // Get server data
      const serverResult = await client.bank.list.query({ characterId });
      const serverItems = new Map(serverResult.items.map((item) => [item.itemId, item]));

      runInAction(() => {
        // Merge server items into local
        for (const [itemId, serverItem] of serverItems) {
          const localItem = this.items.get(itemId);

          if (!localItem) {
            // Server has item we don't have - add it
            this.items.set(itemId, {
              ...serverItem,
              // updatedAt is required now, but server might have old data
              updatedAt: serverItem.updatedAt,
            });
          } else {
            // Both have item - keep newer one (by updatedAt)
            const localUpdatedAt = localItem.updatedAt;
            const serverUpdatedAt = serverItem.updatedAt;

            if (serverUpdatedAt > localUpdatedAt) {
              // Server is newer
              this.items.set(itemId, {
                ...serverItem,
                updatedAt: serverUpdatedAt,
              });
            }
            // Otherwise keep local (it's newer or equal)
          }
        }

        // Note: Items only in local will remain and be pushed to server
      });

      // Push merged local state to server
      await this.pushToServer();

      // Save merged state to localStorage
      this.saveToLocalStorage();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'bank_server_sync' },
      });
    } finally {
      runInAction(() => {
        this.isSyncing = false;
      });
    }
  }

  /**
   * Push local changes to server.
   */
  private async pushToServer(): Promise<void> {
    const characterId = this.rootStore.characterStore.activeCharacter?.id;
    if (!characterId || !this.rootStore.authStore.hasServerUser) {
      return;
    }

    try {
      const client = getVanillaClient();
      const result = await client.bank.sync.mutate({
        characterId,
        items: Array.from(this.items.values()),
      });

      if (result.success) {
        BankStorageService.markSynced(characterId, result.syncedAt);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'bank_push_to_server' },
      });
    }
  }

  /**
   * Queue server sync (debounced).
   */
  private queueServerSync(): void {
    if (!this.rootStore.authStore.hasServerUser) {
      return;
    }

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = window.setTimeout(() => {
      void this.pushToServer();
    }, this.SYNC_DEBOUNCE);
  }

  /**
   * Force immediate sync.
   */
  async syncNow(): Promise<void> {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    await this.syncWithServer();
  }

  // ===========================================================================
  // Reset
  // ===========================================================================

  /**
   * Reset store to initial state.
   */
  reset(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    this.items.clear();
    this.selectedItemId = null;
    this.isLoading = false;
    this.isSyncing = false;
    this.error = null;
  }

  // ===========================================================================
  // Item Selection
  // ===========================================================================

  /** Currently selected item ID */
  selectedItemId: string | null = null;

  /**
   * Select an item.
   *
   * @param itemId - Item ID or null to deselect
   */
  selectItem(itemId: string | null): void {
    this.selectedItemId = itemId;
  }

  /**
   * Get the selected item.
   */
  get selectedItem(): BankItem | null {
    if (!this.selectedItemId) {
      return null;
    }
    return this.items.get(this.selectedItemId) ?? null;
  }

  /**
   * Get all items as array.
   */
  get itemList(): BankItem[] {
    return Array.from(this.items.values());
  }
}
